<template>
	<div>
		<div class="flex flex-wrap">
			<TwoColumns
				first-column-width="w-full lg:w-3/5"
				second-column-width="w-full lg:w-2/5"
				second-column-class="px-5 pb-5 lg:pl-0 bg-white"
				column-gap="md:space-y-8 lg:space-y-0 lg:space-x-5"
				class="w-full"
			>
				<template #first>
					<div
						class="relative flex h-full w-full flex-col items-center justify-center rounded-none bg-gray-100 p-2 md:p-4 lg:rounded-bl-md lg:rounded-tl-md"
					>
						<div
							v-if="totalImages > 1"
							id="vehicle-carousel"
							class="w-full"
						>
							<Carousel
								ref="imageCarouselRef"
								v-bind="galleryConfig"
								v-model="currentSlide"
								class="w-full"
							>
								<Slide
									v-for="(image, index) in images"
									:key="index"
								>
									<div class="carousel__image">
										<div
											class="flex max-h-[500px] cursor-pointer items-center overflow-hidden"
											@click="showLightbox(index)"
										>
											<CarbarImage
												:image="image.file_path"
												:alt-text="
													image.alt_text ??
													`${productDetails.product.year} ${productDetails.product.make} ${productDetails.product.model} image`
												"
												class="pointer-events-none"
											/>
										</div>
									</div>
								</Slide>
								<template #addons>
									<Navigation />
								</template>
							</Carousel>
							<Carousel
								id="thumbnails"
								v-bind="thumbnailsConfig"
								v-model="currentSlide"
							>
								<Slide v-for="index in images" :key="index">
									<template
										#default="{
											currentIndex,
											isActive,
											isVisible,
										}"
									>
										<div
											:class="[
												'thumbnail',
												{ 'is-active': isActive },
												{ 'is-visible': isVisible },
											]"
											@click="slideTo(currentIndex)"
										/>
									</template>
								</Slide>
							</Carousel>
						</div>

						<CarbarImage
							v-else-if="totalImages === 1"
							:image="productDetails.product.primary_image"
							:alt-text="
								productDetails.product.primary_image_alt_text ??
								`${productDetails.product.year} ${productDetails.product.make} ${productDetails.product.model} image`
							"
						/>
						<CarbarImage
							v-else
							image="img/product-catalog/img-comingsoon-xl.webp"
							alt-text="Coming soon"
							class="mix-blend-multiply"
						/>

						<VehicleImageDisclaimer v-if="totalImages >= 1" />
					</div>
				</template>
				<template #second>
					<div class="py-6">
						<BreadcrumbNavigation :crumbs="breadcrumbs" />
					</div>
					<div class="w-full">
						<div class="flex w-full flex-col pl-0">
							<div class="flex justify-between">
								<div class="flex flex-col space-y-2">
									<div class="mb-2 flex gap-1">
										<CarbarTags
											:tags="productDetails.product.tags"
											:discount-set="
												productDetails.product
													.discount_set
											"
											page-type="subscribe"
										/>
									</div>
									<VehicleDetailsLocations
										:location="
											productDetails.product.location
										"
										:available-locations="
											productDetails.product
												.available_locations
										"
									/>
									<h1
										class="mb-0 font-heading text-2xxl font-bold uppercase"
										:data-stock-number="
											productDetails.product.stock_number
										"
									>
										{{ productDetails.product.year }}
										{{ productDetails.product.make }}
										{{ productDetails.product.model }}
										<span v-if="isAppendEV">EV</span>
									</h1>
									<div>
										<div
											class="text-base uppercase text-gray-600"
										>
											<span>
												{{
													productDetails.product
														.badge ?? ''
												}}
											</span>
										</div>
										<div
											class="flex space-x-3 text-base text-gray-600 md:space-x-4"
										>
											<span
												v-if="
													productDetails.product
														.transmission ||
													productDetails.product
														.transmission_type
												"
											>
												{{
													productDetails.product
														.transmission_type ??
													(`${productDetails.product.transmission}`
														.toLowerCase()
														.includes('manual')
														? 'Manual'
														: 'Automatic')
												}}
											</span>
											<span
												v-if="
													productDetails.product
														.body_type
												"
											>
												{{
													productDetails.product
														.body_type
												}}
											</span>
											<span
												v-if="
													productDetails.product
														.fuel_type ||
													productDetails.product.fuel
												"
											>
												{{
													productDetails.product
														.fuel ??
													productDetails.product
														.fuel_type
												}}
											</span>
										</div>
									</div>
								</div>
							</div>
							<VehicleDetailsSubscriptionPricing
								:marker="productDetails.product.pricing.marker"
								:pricing="productDetails.product.pricing.price"
								:upfront-cost="
									productDetails.product.upfront_cost
								"
								:total-min-cost="
									productDetails.product.total_min_cost
								"
								:discount-set="
									productDetails.product.discount_set
								"
								:adjusted-total-min-cost="adjustedTotalMinCost"
								:is-min-term-selected="isMinTermSelected"
								:adjusted-upfront-cost="adjustedUpfrontCost"
								:has-valid-discount-plan="hasValidDiscountPlan"
								:plan-discount="planDiscount"
								:term="currentPlan"
							/>

							<div class="mt-4">
								<TickList
									class="m-0"
									:items="[{ slug: 'convenient' }]"
									icon-vertical-align="items-start"
								>
									<template #convenient="slotProps">
										<div class="flex-row space-x-0 lg:flex">
											<span>
												<span
													class="font-medium text-gray-700"
												>
													Convenient&nbsp;
												</span>
											</span>
											<span>
												- includes insurance, rego,
												roadside and servicing
											</span>
										</div>
									</template>
								</TickList>

								<div class="mt-8">
									<hr />

									<SubscriptionPlans
										v-model="selectedPeriod"
										:plans="
											productDetails.product.service_plans
										"
										@selected-plan="
											getSubscriptionPlanValue
										"
									/>

									<div class="mt-4">
										<p class="py-4 text-center">
											<span
												class="font-medium text-gray-700"
											>
												Order now.&nbsp;
											</span>
											<span
												v-if="
													productDetails.product
														?.get_it_by
														?.estimatedDate
												"
											>
												Get it by
												{{
													formatDate(
														parseDate(
															productDetails
																.product
																.get_it_by
																.estimatedDate,
															'dd-MM-yyyy',
															new Date()
														),
														'dd MMM yy'
													)
												}}
											</span>
										</p>

										<CarbarButton
											class="w-full"
											min-width="min-w-full"
											:rounded="
												whitelabel?.styles?.button
													?.borderRadius
											"
											:size="6"
											:background-color="
												whitelabel?.styles?.button?.bg
											"
											:disabled="isSubscribing"
											:show-loader="isSubscribing"
											@click="checkExistingOrders()"
										>
											{{
												productDetails.product
													.is_available
													? 'Subscribe now'
													: 'Unavailable'
											}}
										</CarbarButton>
									</div>

									<ClientOnly>
										<IncompleteOrderModal
											:is-open="
												productDetails.isIncompleteOrderModalOpen
											"
											:is-resuming-order="
												productDetails.isResumingOrder
											"
											:is-starting-order="
												productDetails.isStartingOrder
											"
											@close="productDetails.onModalClose"
											@resume-order="
												productDetails.onResumeOrder(
													'resumeOrder'
												)
											"
											@start-new-order="
												productDetails.onStartNewOrder
											"
										/>
										<OrderNoticeModal
											:is-open="
												productDetails.isOrderNoticeModalOpen
											"
											:is-vehicle-available="
												productDetails.isVehicleAvailable
											"
											:is-starting-order="
												productDetails.isStartingOrder
											"
											:whitelabel="whitelabel"
											@close="productDetails.onModalClose"
											@start-new-order="
												productDetails.onStartNewOrder
											"
										/>
									</ClientOnly>

									<VehicleDetailsTermsAndConditions
										:partner-key="partnerName"
										:service-plans="
											productDetails.product.service_plans
										"
									/>
								</div>
							</div>
						</div>
					</div>
				</template>
			</TwoColumns>
		</div>
		<VueEasyLightbox
			:visible="productDetails.isLightboxVisible"
			:imgs="lightboxImages"
			:index="productDetails.lightboxImageIndex"
			:loop="true"
			:scroll-disabled="true"
			:move-disabled="true"
			:zoom-disabled="true"
			:rotate-disabled="true"
			:dblclick-disabled="true"
			@hide="onLightboxHide"
			@on-prev="carouselPrev"
			@on-next="carouselNext"
			@on-index-change="
				(oldIndex, newIndex) =>
					(productDetails.lightboxImageIndex = newIndex)
			"
		/>
	</div>
</template>

<script setup>
import CarbarTags from '@/components/badges/CarbarTags.vue'
import BreadcrumbNavigation from '@/components/breadcrumbs/BreadcrumbNavigation.vue'
import CarbarButton from '@/components/buttons/CarbarButton.vue'
import SubscriptionPlans from '@/components/buttons/SubscriptionPlans.vue'
import CarbarImage from '@/components/CarbarImage.vue'
import TickList from '@/components/lists/TickList.vue'
import IncompleteOrderModal from '@/components/modals/subscribe/IncompleteOrder.vue'
import OrderNoticeModal from '@/components/modals/subscribe/OrderNotice.vue'
import TwoColumns from '@/components/sections/TwoColumns.vue'
import VehicleDetailsLocations from '@/components/vehicle-details/VehicleDetailsLocations.vue'
import VehicleImageDisclaimer from '@/components/vehicle-details/VehicleImageDisclaimer.vue'
import VehicleDetailsTermsAndConditions from '@/components/vehicle-details/VehicleDetailsTermsAndConditions.vue'
import { useProductCatalog } from '@/stores/product-catalog'
import { useProductDetails } from '@/stores/product-details'
import { format as formatDate, parse as parseDate } from 'date-fns'
import VueEasyLightbox from 'vue-easy-lightbox'
import { Carousel, Navigation, Slide } from 'vue3-carousel'

const productDetails = useProductDetails()
const productCatalog = useProductCatalog()
const runtimeConfig = useRuntimeConfig()
const route = useRoute()

onMounted(() => {
	productDetails.resetAllStates()
})

const selectedPeriod = ref(null)

const isSubscribing = computed(() => productDetails.isSubscribing)
const isListingOnly = useIsListingOnly()

const isAppendEV = computed(
	() =>
		productDetails.product?.make === 'Jaecoo' &&
		productDetails.product?.model === 'J5' &&
		productDetails.product?.fuel_type === 'Electric'
)

const { whitelabel } = useWhitelabelsStyles()
const { partnerName } = useGetPartnerName()

const breadcrumbs = computed(() => {
	const partner = partnerName.value
	const productTitle = `${productDetails.product.year} ${productDetails.product.make} ${productDetails.product.model}`

	if (partner) {
		return [
			{ title: 'Home', url: `${whitelabel.value?.links?.partnerUrl}` },
			{
				title: 'Browse',
				url: `${whitelabel.value?.links?.partnerUrl}/browse`,
			},
			{ title: productTitle },
		]
	}

	return [
		{ title: 'Home', url: '/' },
		{ title: 'Cars', url: '/cars/subscribe' },
		{ title: 'Subscribe', url: '/cars/subscribe' },
		{ title: productTitle },
	]
})

const imageCarouselRef = ref(null)

const slideTo = (nextSlide) => (currentSlide.value = nextSlide)

const galleryConfig = {
	itemsToShow: 1,
	wrapAround: true,
	slideEffect: 'slide',
	mouseDrag: {
		threshold: 0.1,
	},
	touchDrag: true,
}

const thumbnailsConfig = ref({
	itemsToShow: 7,
	wrapAround: true,
	touchDrag: false,
	snapAlign: 'center',
})

const images = computed(() => {
	let filledImages = productDetails.product.largeImages
	if (!filledImages || filledImages.length === 0) {
		return []
	}

	if (filledImages.length > 6) {
		return filledImages
	}

	while (filledImages.length < 6) {
		filledImages = [...filledImages, ...productDetails.product.largeImages]
	}

	return filledImages
})

const currentSlide = ref(0)

const totalImages = productDetails.product.largeImages.length

const lightboxImages = computed(() =>
	images.value.map((image) => ({
		src: image.file_path,
		title: image.file_name,
	}))
)

const carouselPrev = () => {
	imageCarouselRef.value.prev()
}

const carouselNext = () => {
	imageCarouselRef.value.next()
}

const showLightbox = (index) => {
	productDetails.lightboxImageIndex = index
	productDetails.isLightboxVisible = true

	productDetails.gtmTrack({
		event: 'subscription_car_image',
		product_category: 'Subscription',
		product: productDetails.product,
		page: productCatalog.productsPagination.currentPage,
		subscription_car_primary_url: runtimeConfig.public.APP_URL + route.path,
	})
}

const onLightboxHide = () => {
	productDetails.isLightboxVisible = false

	imageCarouselRef.value.slideTo(productDetails.lightboxImageIndex)
}

const checkExistingOrders = async () => {
	if (isListingOnly) {
		// Carry the plan picked above (months of minimum term, or null for the standard plan) to the form.
		await navigateTo({
			path: `/order-request/${productDetails.product.webref_code}-${productDetails.product.stock_id}`,
			query: currentPlan.value ? { plan: String(currentPlan.value) } : {},
		})
		return
	}

	productDetails.gtmTrack({
		event: 'subscription_get_started',
		product_category: 'Subscription',
		product: productDetails.product,
		page: productCatalog.productsPagination.currentPage,
		subscription_car_primary_url: runtimeConfig.public.APP_URL + route.path,
	})

	await productDetails.checkExistingOrders()
}

// Get subscription plan and adjust pricing and discount accordingly.
// null refers to standard subscription.
const currentPlan = ref(null)
const getSubscriptionPlanValue = (term) => {
	currentPlan.value = term
}

const {
	adjustedUpfrontCost,
	adjustedTotalMinCost,
	planDiscount,
	isMinTermSelected,
	hasValidDiscountPlan,
} = useSubscriptionPlanAndAdjustPricing(currentPlan)

// const hasMinTermPlans = computed(() => {
// 	return Object.keys(productDetails.product.service_plans).length > 1
// })

// const showMinTermFootnote = computed(() => {
// 	const discount =
// 		productDetails.product?.service_plans?.subs_12m_plan?.discount

// 	return !!discount
// })
</script>
